import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useContent } from '../siteContent';

/**
 * Forms are received by Netlify Forms. The matching static definitions live in /public/forms.html,
 * which Netlify scans at deploy time. Submissions are posted to that static file so the SPA
 * rewrite never intercepts them.
 */
export const FORM_ENDPOINT = '/forms.html';

export type FieldDef = {
  name: string;
  label: string;
  type?: 'text' | 'tel' | 'email' | 'select' | 'textarea';
  required?: boolean;
  optional?: boolean;
  autoComplete?: string;
  inputMode?: 'tel' | 'email' | 'text' | 'numeric';
  options?: string[];
  placeholder?: string;
  hint?: string;
  maxLength?: number;
  wide?: boolean;
  validate?: (value: string) => string | null;
};

export const normalizeDigits = (value: string) => value.replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit))).replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)));

export const validators = {
  saudiMobile: (value: string) => {
    const digits = normalizeDigits(value).replace(/[\s()-]/g, '');
    return /^(?:\+?966|00966|0)?5\d{8}$/.test(digits) ? null : 'أدخل رقم جوال سعودي صحيح، مثل 05XXXXXXXX.';
  },
  email: (value: string) => (!value || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim()) ? null : 'أدخل بريدًا إلكترونيًا صحيحًا'),
  contact: (value: string) => {
    const clean = normalizeDigits(value).replace(/[\s()-]/g, '');
    if (/@/.test(value)) return validators.email(value);
    return /^(?:\+?966|00966|0)?5\d{8}$/.test(clean) ? null : 'أدخل رقم جوال سعودي أو بريدًا إلكترونيًا صحيحًا';
  },
};

type Status = 'idle' | 'sending' | 'sent' | 'error';

export function NetlifyForm({ name, fields, submitLabel, success, privacyNote }: { name: string; fields: FieldDef[]; submitLabel: string; success: ReactNode; privacyNote?: ReactNode }) {
  const { settings } = useContent();
  const email = settings.supportEmail;
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(fields.map(field => [field.name, ''])));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');

  useEffect(() => {
    // Without JavaScript the browser posts the form natively and Netlify returns here with ?sent=1.
    if (new URLSearchParams(window.location.search).get('sent') === '1') setStatus('sent');
  }, []);

  const check = (field: FieldDef, value: string) => {
    const trimmed = value.trim();
    if (field.required && !trimmed) return field.type === 'select' ? 'اختر أحد الخيارات' : 'هذا الحقل مطلوب';
    if (trimmed && field.validate) return field.validate(trimmed);
    return null;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const nextErrors: Record<string, string> = {};
    for (const field of fields) { const error = check(field, values[field.name]); if (error) nextErrors[field.name] = error; }
    setErrors(nextErrors);
    const firstInvalid = fields.find(field => nextErrors[field.name]);
    if (firstInvalid) { form.querySelector<HTMLElement>(`[name="${firstInvalid.name}"]`)?.focus(); return; }
    setStatus('sending');
    const body = new URLSearchParams({ 'form-name': name, 'bot-field': (form.elements.namedItem('bot-field') as HTMLInputElement | null)?.value || '' });
    for (const field of fields) body.append(field.name, normalizeDigits(values[field.name].trim()));
    try {
      const response = await fetch(FORM_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      if (!response.ok) throw new Error(String(response.status));
      setStatus('sent');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setStatus('error');
    }
  };

  if (!settings.formsEnabled) return <p className="mx-form-alert mx-form-off">استقبال الطلبات عبر النموذج متوقف حاليًا، راسلنا على <a href={`mailto:${email}`} dir="ltr">{email}</a></p>;
  if (status === 'sent') return <div className="mx-form-success" role="status" tabIndex={-1} ref={node => node?.focus()}>{success}</div>;

  return <form className="mx-form" name={name} method="POST" action={`${FORM_ENDPOINT}`} noValidate onSubmit={onSubmit}>
    <input type="hidden" name="form-name" value={name} />
    <p className="mx-hp" aria-hidden="true"><label>لا تملأ هذا الحقل<input name="bot-field" tabIndex={-1} autoComplete="off" /></label></p>
    <div className="mx-form-grid">
      {fields.map(field => {
        const id = `${name}-${field.name}`; const error = errors[field.name]; const describedBy = [field.hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ') || undefined;
        const common = { id, name: field.name, required: field.required, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy, value: values[field.name], autoComplete: field.autoComplete,
          onChange: (event: { target: { value: string } }) => { const value = event.target.value; setValues(current => ({ ...current, [field.name]: value })); if (error) setErrors(current => { const next = { ...current }; const again = check(field, value); if (again) next[field.name] = again; else delete next[field.name]; return next; }); },
          onBlur: () => { const again = check(field, values[field.name]); setErrors(current => { const next = { ...current }; if (again && values[field.name]) next[field.name] = again; else if (!again) delete next[field.name]; return next; }); } };
        return <div key={field.name} className={`mx-field${field.wide || field.type === 'textarea' ? ' mx-field-wide' : ''}${error ? ' has-error' : ''}`}>
          <label htmlFor={id}>{field.label}{field.optional && <span className="mx-optional"> (اختياري)</span>}</label>
          {field.type === 'select'
            ? <select {...common}><option value="" disabled>اختر</option>{field.options?.map(option => <option key={option} value={option}>{option}</option>)}</select>
            : field.type === 'textarea'
              ? <textarea {...common} rows={4} maxLength={field.maxLength ?? 1500} placeholder={field.placeholder} />
              : <input {...common} type={field.type ?? 'text'} inputMode={field.inputMode} placeholder={field.placeholder} maxLength={field.maxLength ?? 120} dir={field.type === 'email' || field.type === 'tel' ? 'ltr' : undefined} />}
          {field.hint && <small id={`${id}-hint`} className="mx-hint">{field.hint}</small>}
          {error && <small id={`${id}-error`} className="mx-error">{error}</small>}
        </div>;
      })}
    </div>
    {status === 'error' && <p className="mx-form-alert" role="alert">تعذّر إرسال النموذج الآن، تحقق من اتصالك وحاول مرة أخرى، أو راسلنا مباشرة على <a href={`mailto:${email}`} dir="ltr">{email}</a></p>}
    <div className="mx-form-foot">
      <button type="submit" className="mx-btn mx-btn-primary" disabled={status === 'sending'}>{status === 'sending' ? 'جارٍ الإرسال…' : submitLabel}</button>
      {privacyNote && <small className="mx-form-note">{privacyNote}</small>}
    </div>
  </form>;
}
